import React, { useState } from 'react';

/**
 * Tiroir « Aide en jeu » — fidèle à l'esprit du prototype (`tutorial.js` drawerAide) :
 * chaque fiche décrit une règle réellement appliquée par le jeu. Recherche plein texte.
 */

interface Bloc {
  h: string;
  p: string;
}
interface Fiche {
  ico: string;
  titre: string;
  blocs: Bloc[];
}

const AIDE: Fiche[] = [
  {
    ico: '🎧',
    titre: 'Prise d’appels',
    blocs: [
      {
        h: 'OFF par défaut',
        p: 'La prise d’appels est désactivée à l’arrivée : aucune intervention ne se crée toute seule. Activez-la depuis la topbar pour recevoir des appels — c’est vous qui pilotez le rythme.',
      },
      {
        h: 'File opérationnelle',
        p: 'Les appels pris arrivent dans la colonne de gauche, filtrables par Attente / En cours / Toutes. Cliquez une carte pour la sélectionner et travailler dessus.',
      },
    ],
  },
  {
    ico: '🚒',
    titre: 'Engagement des engins',
    blocs: [
      {
        h: 'Sélection puis envoi',
        p: 'Sélectionnez un ou plusieurs engins disponibles dans le moniteur, puis engagez-les sur l’intervention sélectionnée. Les moyens requis sont indiqués sur la carte (ex. 2/3).',
      },
      {
        h: 'Statuts',
        p: 'Un engin passe disponible → en transit → sur les lieux → de retour. Un engin en maintenance ou en panne n’est pas engageable tant qu’il n’est pas remis en service.',
      },
    ],
  },
  {
    ico: '🧑‍🚒',
    titre: 'Armement & équipage',
    blocs: [
      {
        h: 'Compétences obligatoires',
        p: 'Un véhicule ne part que si son équipage couvre les compétences obligatoires de l’engin. Les compétences viennent du grade au recrutement (socle) et des spécialités acquises en formation.',
      },
      {
        h: 'Véhicule bloqué',
        p: 'Si une compétence obligatoire n’est pas couverte par les agents à bord, l’engin reste bloqué. Vérifiez le planning (agent présent/actif) et l’armement du véhicule.',
      },
    ],
  },
  {
    ico: '🗓️',
    titre: 'Planning du personnel',
    blocs: [
      {
        h: 'Présence',
        p: 'Un agent doit être présent et actif pour armer un engin. Le planning (tiroir Planning) définit qui est de garde. Un agent absent n’apparaît pas dans les équipages disponibles.',
      },
    ],
  },
  {
    ico: '🏢',
    titre: 'Sites & réseau',
    blocs: [
      {
        h: 'Gérer un centre',
        p: 'Le tiroir Sites liste vos centres. Cliquez-en un pour gérer personnel, véhicules et planning. Le tiroir Réseau donne la vue d’ensemble du parc et des effectifs.',
      },
    ],
  },
  {
    ico: '🔬',
    titre: 'Technologies',
    blocs: [
      {
        h: 'Recherche',
        p: 'L’arbre technologique se paie en argent (jamais en médailles pour débloquer). Une seule recherche à la fois ; les médailles servent uniquement à terminer un chantier immédiatement.',
      },
    ],
  },
];

const AideDrawer: React.FC = () => {
  const [q, setQ] = useState('');
  const ql = q.trim().toLowerCase();
  const fiches = AIDE.map((f) => ({
    ...f,
    blocs: f.blocs.filter((b) => !ql || `${b.h} ${b.p}`.toLowerCase().includes(ql)),
  })).filter((f) => f.blocs.length || (ql && f.titre.toLowerCase().includes(ql)));

  return (
    <>
      <p className="dintro">
        Tout ce qui est écrit ici correspond à une règle réellement appliquée par le jeu. Si un engin
        refuse de partir ou qu’un agent n’apparaît pas dans une liste, la réponse est dans « Armement »
        ou dans « Planning ».
      </p>

      <div className="aidebar">
        <input
          type="search"
          placeholder="Rechercher dans l'aide…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoComplete="off"
        />
      </div>

      {fiches.length ? (
        <div className="aidegrid">
          {fiches.map((f) => (
            <section className="aide" key={f.titre}>
              <header className="aide__h">
                <span className="aide__ico">{f.ico}</span>
                {f.titre}
              </header>
              <div className="aide__b">
                {f.blocs.map((b, i) => (
                  <div className="aidebloc" key={i}>
                    <p className="aidebloc__h">{b.h}</p>
                    <p className="aidebloc__p">{b.p}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="empty">Rien ne correspond à cette recherche.</div>
      )}
    </>
  );
};

export default AideDrawer;
